import type { LlmUIMessageParts } from '@azymmetric/shared/types/llm.types';
import { Button } from '@components/ui/button';
import { useArtifactStore } from '@stores/artifact.store';
import { ChartBar, Eye } from 'lucide-react';
import { MessageContent } from '../../../../ai-elements/message';
import { BlueLabel } from '../../../../shared/blue-label.component';
import { Spinner } from '../../../../shared/spinner.component';

export type GenerateSankeyToolProps = {
  messageId: string;
  part: Extract<LlmUIMessageParts, { type: 'tool-generateSankey' }>;
  partIndex: number;
};

export const GenerateSankeyTool: React.FC<GenerateSankeyToolProps> = (props) => {
  const { messageId, part, partIndex } = props;
  const { setArtifact } = useArtifactStore();

  // Helper function to parse and extract user-friendly error messages
  const parseErrorMessage = (errorText: string): string[] => {
    try {
      // Try to extract validation errors from the error text
      const validationErrorMatch = errorText.match(/Error message: (\[.*\])/);
      if (validationErrorMatch) {
        const validationErrors = JSON.parse(validationErrorMatch[1]);
        return validationErrors.map((error: { path: string[]; message: string }) => {
          const fieldName = error.path.join('.');
          return fieldName ? `${fieldName}: ${error.message}` : error.message;
        });
      }

      // If we can't parse validation errors, look for common error patterns
      if (errorText.includes('Type validation failed')) {
        return ['The chart data did not meet the required format. Please try rephrasing your request.'];
      }

      if (errorText.includes('Invalid input')) {
        return ['Invalid chart configuration. Please try a different request.'];
      }

      // Generic fallback
      return ['Failed to generate chart. Please try again or rephrase your request.'];
    } catch {
      // If parsing fails, return a generic message
      return ['An unexpected error occurred while generating the chart.'];
    }
  };

  // Output available state
  if (part.state === 'output-available' && 'output' in part) {
    const sankeyData = part.output;

    // If the data is invalid, show error state
    if (!sankeyData.valid) {
      return (
        <MessageContent key={`${messageId}-${partIndex}`}>
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
            <div className="mb-2 flex items-center gap-2">
              <ChartBar className="size-4 text-destructive" />
              <p className="font-medium text-destructive text-sm">Failed to generate Sankey chart</p>
            </div>
            {sankeyData.errors && sankeyData.errors.length > 0 && (
              <ul className="space-y-1 text-sm text-destructive">
                {sankeyData.errors.map((error, idx) => (
                  <li key={idx}>• {error.message}</li>
                ))}
              </ul>
            )}
          </div>
        </MessageContent>
      );
    }

    // Valid data - show success state with optional warnings
    return (
      <MessageContent key={`${messageId}-${partIndex}`}>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <div className="rounded-md bg-[#3D2EFF1A] p-2 text-[#3D2EFF]">
                <ChartBar className="size-4" />
              </div>
              <div>
                <div className="text-sm font-medium">{sankeyData.title}</div>
                <BlueLabel text="Sankey Chart" />
              </div>
            </div>
            {sankeyData.errors && sankeyData.errors.length > 0 && (
              <div className="rounded-lg border border-amber-500 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900 mb-1">Warnings:</p>
                <ul className="space-y-1 text-sm text-amber-800">
                  {sankeyData.errors.map((error, idx) => (
                    <li key={idx}>• {error.message}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setArtifact({
                    content: sankeyData,
                    kind: 'sankey',
                    status: 'idle',
                    title: sankeyData.title,
                  });
                }}
                size="sm"
                variant="primary"
              >
                <Eye className="mr-1 size-3" />
                View
              </Button>
            </div>
          </div>
        </div>
      </MessageContent>
    );
  }

  // Error state
  if (part.state === 'output-error' && 'errorText' in part) {
    const userFriendlyErrors = parseErrorMessage(part.errorText);

    return (
      <MessageContent key={`${messageId}-${partIndex}`}>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <div className="mb-2 flex items-center gap-2">
            <ChartBar className="size-4 text-destructive" />
            <p className="font-medium text-destructive text-sm">Failed to generate Sankey chart</p>
          </div>
          <ul className="space-y-1 text-sm text-destructive">
            {userFriendlyErrors.map((error, idx) => (
              <li key={idx}>• {error}</li>
            ))}
          </ul>
        </div>
      </MessageContent>
    );
  }

  // Loading state
  if (part.state === 'input-streaming' || part.state === 'input-available') {
    return (
      <MessageContent key={`${messageId}-${partIndex}`}>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2">
            <Spinner size="sm" />
            <span className="text-sm text-muted-foreground">Generating Sankey chart...</span>
          </div>
        </div>
      </MessageContent>
    );
  }

  return null;
};
